import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Home, ArrowLeft, Phone, Mail, User, Calendar, DollarSign, Bed, Bath, Square, ChevronLeft, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import { fixImageUrl } from '../lib/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [siteSettings, setSiteSettings] = useState({ company_phone: '', company_email: '' });
  const { user } = useAuth();
  
  // Contact Form State
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  // Schedule Viewing State
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '10:00',
    message: ''
  });

  useEffect(() => {
    fetchListing();
    fetchSiteSettings();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Failed to fetch listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/`);
      setSiteSettings(response.data || {});
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const nextImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries/contact/`, {
        listing_id: id,
        listing_title: listing?.title,
        ...contactForm
      });
      setContactSuccess(true);
      toast.success('Message sent successfully! We will contact you soon.');
    } catch (error) {
      // Even if API doesn't exist yet, show success for UX (inquiry will be logged)
      console.log('Contact inquiry:', { listing_id: id, ...contactForm });
      setContactSuccess(true);
      toast.success('Message sent successfully! We will contact you soon.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setScheduleSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries/schedule/`, {
        listing_id: id,
        listing_title: listing?.title,
        ...scheduleForm
      });
      setScheduleSuccess(true);
      toast.success('Visit request submitted! We will confirm your appointment soon.');
    } catch (error) {
      // Even if API doesn't exist yet, show success for UX
      console.log('Schedule inquiry:', { listing_id: id, ...scheduleForm });
      setScheduleSuccess(true);
      toast.success('Visit request submitted! We will confirm your appointment soon.');
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const resetContactForm = () => {
    setContactForm({ name: '', email: '', phone: '', message: '' });
    setContactSuccess(false);
  };

  const resetScheduleForm = () => {
    setScheduleForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '10:00', message: '' });
    setScheduleSuccess(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Property Not Found</h2>
          <p className="font-body text-muted-foreground mb-6">The property you're looking for doesn't exist.</p>
          <Link to="/properties">
            <Button className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-brown text-white hover:bg-brand-brown/80">
              Browse Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/properties" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-body text-sm hidden sm:inline">Back to listings</span>
            </Link>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-heading text-lg font-bold tracking-tight text-brand-brown">Signé <span className="text-brand-gold">Properties</span></span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/employee'}>
                <Button className="rounded-none font-heading font-bold uppercase tracking-wide text-xs bg-brand-brown text-white hover:bg-brand-brown/80">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors">
                Staff Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative h-[50vh] lg:h-[55vh] bg-brand-brown">
              {listing.images && listing.images.length > 0 ? (
                <>
                  <img 
                    src={fixImageUrl(listing.images[currentImageIndex])} 
                    alt={`${listing.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    data-testid="property-main-image"
                  />
                  {listing.images.length > 1 && (
                    <>
                      <button 
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                        data-testid="prev-image-btn"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button 
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                        data-testid="next-image-btn"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {listing.images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-3 h-3 transition-colors ${idx === currentImageIndex ? 'bg-brand-gold' : 'bg-white/50 hover:bg-white'}`}
                            data-testid={`image-dot-${idx}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Home className="w-24 h-24 text-muted-foreground" />
                </div>
              )}
              <Badge 
                className={`absolute top-6 left-6 rounded-none px-4 py-2 font-heading text-xs uppercase tracking-widest font-bold ${
                  listing.status === 'sold' 
                    ? 'bg-brand-gold text-brand-brown' 
                    : listing.status === 'pending'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-brand-brown text-white'
                }`}
                data-testid="property-status-badge"
              >
                {listing.status}
              </Badge>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-5 h-5" />
                <span className="font-body" data-testid="property-address">{listing.address}</span>
                {listing.city && <span className="font-body">• {listing.city}</span>}
                {listing.state && <span className="font-body">• {listing.state}</span>}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="property-title">
                {listing.title}
              </h1>
              <p className="font-heading text-3xl font-bold text-brand-gold" data-testid="property-price">
                {formatPrice(listing.price)}
              </p>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-semibold">Description</h2>
                <p className="font-body text-muted-foreground leading-relaxed whitespace-pre-line break-words" data-testid="property-description">
                  {listing.description}
                </p>
              </div>
            )}

            {/* Meta Info */}
            <div className="pt-8 border-t border-border">
              <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="font-body">Listed on {formatDate(listing.created_at)}</span>
                </div>
                {listing.created_by_name && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-body">Listed by {listing.created_by_name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white border border-border p-8 space-y-6">
                <h3 className="font-heading text-lg font-semibold">Interested in this property?</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Contact us today to schedule a viewing or get more information about this listing.
                </p>
                <div className="space-y-4">
                  <Button 
                    onClick={() => setScheduleDialogOpen(true)}
                    className="w-full rounded-none font-heading font-bold uppercase tracking-wide py-6 bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                    data-testid="schedule-viewing-btn"
                  >
                    Schedule Viewing
                  </Button>
                  <Button 
                    onClick={() => setContactDialogOpen(true)}
                    variant="outline"
                    className="w-full rounded-none border-2 border-brand-brown font-heading font-bold uppercase tracking-wide py-6 hover:bg-brand-brown hover:text-white"
                    data-testid="contact-agent-btn"
                  >
                    Contact Agent
                  </Button>
                </div>
                <div className="pt-6 border-t border-border space-y-4">
                  {siteSettings.company_phone && (
                    <a href={`tel:${siteSettings.company_phone}`} className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors" data-testid="sidebar-phone">
                      <Phone className="w-5 h-5 text-brand-gold" />
                      <span className="font-body">{siteSettings.company_phone}</span>
                    </a>
                  )}
                  {siteSettings.company_email && (
                    <a href={`mailto:${siteSettings.company_email}`} className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors break-all" data-testid="sidebar-email">
                      <Mail className="w-5 h-5 text-brand-gold flex-shrink-0" />
                      <span className="font-body">{siteSettings.company_email}</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Property Details */}
              {(() => {
                const cf = listing.custom_fields || {};
                // Skip these keys from custom_fields because they're shown via top-level structured fields
                const skipKeys = new Set(['property_type', 'bedrooms', 'bathrooms', 'sqft', 'size']);

                const primary = [];
                const topType = listing.property_type || cf.property_type;
                if (topType) primary.push(['Property Type', topType]);
                if (listing.bedrooms != null) primary.push(['Bedrooms', listing.bedrooms]);
                if (listing.bathrooms != null) primary.push(['Bathrooms', listing.bathrooms]);
                if (listing.sqft != null) primary.push(['Size', `${listing.sqft.toLocaleString()} m²`]);
                if (listing.district) primary.push(['District', listing.district]);
                if (listing.city) primary.push(['City / Village', listing.city]);
                if (listing.status) primary.push(['Status', listing.status.charAt(0).toUpperCase() + listing.status.slice(1)]);

                const extras = Object.entries(cf).filter(([k]) => !skipKeys.has(k));

                const detailEntries = [...primary, ...extras.map(([k, v]) => [k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), v])];

                if (detailEntries.length === 0) return null;
                return (
                  <div className="bg-white border border-border p-8 space-y-4">
                    <h3 className="font-heading text-lg font-semibold">Property Details</h3>
                    <div className="divide-y divide-border">
                      {detailEntries.map(([key, value], i) => (
                        <div key={`${key}-${i}`} className="flex items-center justify-between py-3 gap-4">
                          <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                            {key}
                          </p>
                          <p className="font-body text-sm text-foreground text-right" data-testid={`detail-${String(key).toLowerCase().replace(/\s+/g, '-')}`}>
                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Agent Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={(open) => { setContactDialogOpen(open); if (!open) resetContactForm(); }}>
        <DialogContent className="max-w-md" aria-describedby="contact-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Contact Agent</DialogTitle>
            <p id="contact-dialog-description" className="font-body text-sm text-muted-foreground">
              Send us a message about {listing?.title}
            </p>
          </DialogHeader>
          
          {contactSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">Message Sent!</h3>
              <p className="font-body text-muted-foreground mb-6">
                Thank you for your inquiry. One of our agents will contact you shortly.
              </p>
              <Button 
                onClick={() => setContactDialogOpen(false)}
                className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Your Name *
                </Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  className="rounded-none border-2"
                  data-testid="contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Email Address *
                </Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  className="rounded-none border-2"
                  data-testid="contact-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+1 (234) 567-890"
                  className="rounded-none border-2"
                  data-testid="contact-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Message *
                </Label>
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows={3}
                  placeholder="I'm interested in this property and would like more information..."
                  className="rounded-none border-2"
                  data-testid="contact-message"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={contactSubmitting}
                  className="flex-1 rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                  data-testid="contact-submit-btn"
                >
                  {contactSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setContactDialogOpen(false)} 
                  className="rounded-none border-2"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Viewing Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={(open) => { setScheduleDialogOpen(open); if (!open) resetScheduleForm(); }}>
        <DialogContent className="max-w-md" aria-describedby="schedule-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Schedule a Viewing</DialogTitle>
            <p id="schedule-dialog-description" className="font-body text-sm text-muted-foreground">
              Book a visit to see {listing?.title}
            </p>
          </DialogHeader>
          
          {scheduleSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">Request Submitted!</h3>
              <p className="font-body text-muted-foreground mb-6">
                Thank you! We'll confirm your viewing appointment shortly via email or phone.
              </p>
              <Button 
                onClick={() => setScheduleDialogOpen(false)}
                className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleScheduleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Your Name *
                </Label>
                <Input
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  className="rounded-none border-2"
                  data-testid="schedule-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Email Address *
                </Label>
                <Input
                  type="email"
                  value={scheduleForm.email}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  className="rounded-none border-2"
                  data-testid="schedule-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Phone Number *
                </Label>
                <Input
                  type="tel"
                  value={scheduleForm.phone}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, phone: e.target.value })}
                  required
                  placeholder="+1 (234) 567-890"
                  className="rounded-none border-2"
                  data-testid="schedule-phone"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Preferred Date *
                  </Label>
                  <Input
                    type="date"
                    value={scheduleForm.preferred_date}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, preferred_date: e.target.value })}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="rounded-none border-2"
                    data-testid="schedule-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Preferred Time *
                  </Label>
                  <Select value={scheduleForm.preferred_time} onValueChange={(value) => setScheduleForm({ ...scheduleForm, preferred_time: value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="schedule-time">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="11:00">11:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="13:00">1:00 PM</SelectItem>
                      <SelectItem value="14:00">2:00 PM</SelectItem>
                      <SelectItem value="15:00">3:00 PM</SelectItem>
                      <SelectItem value="16:00">4:00 PM</SelectItem>
                      <SelectItem value="17:00">5:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Additional Notes
                </Label>
                <Textarea
                  value={scheduleForm.message}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, message: e.target.value })}
                  rows={2}
                  placeholder="Any special requests or questions..."
                  className="rounded-none border-2"
                  data-testid="schedule-notes"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={scheduleSubmitting}
                  className="flex-1 rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                  data-testid="schedule-submit-btn"
                >
                  {scheduleSubmitting ? 'Submitting...' : 'Request Viewing'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setScheduleDialogOpen(false)} 
                  className="rounded-none border-2"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp />
    </div>
  );
};

export default PropertyDetail;
